import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  input,
} from '@angular/core';
import { QaroniCustomCanvasColorPlugin } from '@qaroni-shared/qaroni-plugins-charts/qaroni-custom-canva-color-chart-plugin';
import { QaroniRequiredProgressBarPlugin } from '@qaroni-shared/qaroni-plugins-charts/qaroni-required-progress-bar-chart-plugin';
import { Chart, ChartData, ChartOptions, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

@Component({
  selector: 'qaroni-assessment-progress-bar-preview',
  templateUrl: './assessment-progress-bar-preview.component.html',
  styleUrls: ['./assessment-progress-bar-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AssessmentProgressBarPreviewComponent
  implements AfterViewInit, OnDestroy
{
  @ViewChild('ProgressBar') progressBarChart!: ElementRef<HTMLCanvasElement>;

  readonly progressObtained = input<number>(0);
  readonly progressRequired = input<number>(0);
  readonly progressBarColor = input<'blue' | 'red'>('blue');
  readonly fullWidth = input<boolean>(false);

  private chart: Chart<'bar'> | null = null;
  private progressBarCanvas: HTMLCanvasElement | null = null;
  private colors = new Map<string, string>();

  constructor() {
    this.colors.set('blue', '#5CB3F0');
    this.colors.set('red', '#DF6F6F');

    Chart.register(ChartDataLabels);
    Chart.register(...registerables);
  }

  ngAfterViewInit() {
    this.progressBarCanvas = this.progressBarChart.nativeElement;
    this.adjustCanvasPixelRatio();
    this.chartProgressBarSetup();
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  private adjustCanvasPixelRatio() {
    const canvas = this.progressBarCanvas;
    const context = canvas.getContext('2d');
    const devicePixelRatio = window.devicePixelRatio || 1;

    canvas.width = canvas.offsetWidth * devicePixelRatio;
    canvas.height = canvas.offsetHeight * devicePixelRatio;

    if (context) {
      context.scale(devicePixelRatio, devicePixelRatio);
    }
  }

  private chartProgressBarSetup() {
    if (this.chart) {
      this.chart.destroy();
    }

    const chartOptions: ChartOptions<'bar'> = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      scales: {
        x: {
          display: false,
          beginAtZero: true,
          max: 100,
        },
        y: {
          display: false,
          grid: {
            display: false,
          },
          ticks: {
            display: false,
          },
        },
      },
      plugins: {
        datalabels: {
          formatter: (value: number) => {
            void value;
            return '';
          },
        },
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
    };

    const chartData: ChartData<'bar'> = {
      labels: ['Progress'],
      datasets: [
        {
          label: 'Progress Obtained',
          data: [this.progressObtained()],
          backgroundColor: this.colors.get(this.progressBarColor()),
          borderRadius: 2,
          barPercentage: 0.5,
        },
      ],
    };

    this.chart = new Chart(this.progressBarCanvas, {
      type: 'bar',
      data: chartData,
      options: chartOptions,
      plugins: [
        new QaroniRequiredProgressBarPlugin(this.progressRequired()),
        new QaroniCustomCanvasColorPlugin(),
      ],
    });
  }
}
